import { useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { isApiError, getFieldErrors } from '@/services/api';
import type { ServiceFormInput, ServiceFormSubmitValues } from '../schemas/service.schema';

const SERVER_MAPPED_FIELDS = [
    'name', 'description', 'status', 'cost_mode', 'vat_category_id',
    'total_cost_net',
    'position', 'service_type', 'months_from_start', 'mileage_from_start',
] as const;

type InclusionField = 'item_id' | 'unit_quantity' | 'unit_price_net';
const INCLUSION_ERROR_KEY = /^items\.(\d+)\.(item_id|unit_quantity|unit_price_net)$/;

interface UseServiceFormSubmitArgs {
    onSubmit: (values: ServiceFormSubmitValues) => Promise<unknown>;
    setError: UseFormSetError<ServiceFormInput>;
    existingCostMode?: string;
    inclusionCount: number;
    genericErrorMessage: string;
}

export function useServiceFormSubmit({
    onSubmit,
    setError,
    existingCostMode,
    inclusionCount,
    genericErrorMessage,
}: UseServiceFormSubmitArgs) {
    const [genericError, setGenericError] = useState<string | null>(null);
    const [pendingConversion, setPendingConversion] = useState<ServiceFormSubmitValues | null>(null);

    const persist = async (data: ServiceFormSubmitValues) => {
        setGenericError(null);
        try {
            await onSubmit(data);
        } catch (err) {
            if (isApiError(err) && err.status === 422) {
                const fields = getFieldErrors(err);
                let mappedAnyField = false;
                SERVER_MAPPED_FIELDS.forEach((field) => {
                    const message = fields[field]?.[0];
                    if (message) {
                        setError(field, { type: 'server', message });
                        mappedAnyField = true;
                    }
                });
                Object.entries(fields).forEach(([key, messages]) => {
                    const match = INCLUSION_ERROR_KEY.exec(key);
                    const message = messages?.[0];
                    if (!match || !message) return;
                    setError(`items.${Number(match[1])}.${match[2] as InclusionField}`, { type: 'server', message });
                    mappedAnyField = true;
                });
                if (mappedAnyField) return;
            }
            setGenericError(genericErrorMessage);
        }
    };

    const submit = async (data: ServiceFormSubmitValues) => {
        const isDestructiveConversion =
            existingCostMode === 'breakdown' && data.cost_mode === 'total' && inclusionCount > 0;
        if (isDestructiveConversion) {
            setPendingConversion(data);
            return;
        }
        await persist(data);
    };

    const confirmConversion = async () => {
        if (!pendingConversion) return;
        await persist({ ...pendingConversion, confirm_inclusions_deletion: true });
        setPendingConversion(null);
    };

    return {
        submit,
        genericError,
        pendingConversion,
        confirmConversion,
        cancelConversion: () => setPendingConversion(null),
    };
}
